/**
 * UpSurv 2.0 — THE ONE PLACE THE ENGINES ARE CALLED FROM THE UI.
 *
 * `computeResults(doc)` runs the four engines over a document and indexes their
 * output so a screen can ask "what does the form say about E1a in this room?"
 * without calling an engine, and without ever assembling an answer key by hand:
 *
 *   visibility  ← isVisible()      (src/core/validation.ts)
 *   quantities  ← computeDerived() (src/core/calc.ts)
 *   validation  ← validate()       (src/core/validation.ts)
 *   gaps        ← detectGaps()     (src/core/validation.ts)
 *
 * A component that re-derives any of these is a defect — it is how the form and
 * the estimate drift apart. If a screen needs a number the engines do not
 * expose, the fix is a new export from `src/core`, not arithmetic in a `.tsx`.
 *
 * Pure: same document in, same indices out. No clock, no store, no React. That
 * is what lets tests/unit/ui-shell.test.ts exercise it in the node environment.
 *
 * ── Scope narrowing ──────────────────────────────────────────────────────────
 * Every lookup key goes through `conditionScope()` (catalog.ts), the same
 * narrowing the validation and calc engines use. A lobby screen asking about B8
 * with `{roomId, groupId}` gets `{}` and finds the building answer; asking with
 * the raw scope would build "room:r1|grp:gA|B8", a key that can never exist.
 */

import {
  DERIVED_ONLY_QUESTION_IDS,
  STRUCTURAL_QUESTION_IDS,
  byId,
  conditionScope,
  questionsFor,
} from '../../core/catalog';
import { computeDerived } from '../../core/calc';
import { buildSurveySections } from '../../core/sections';
import {
  BLOCKING_GAP_REASONS,
  detectGapsWithIssues,
  isAnsweredFor,
  isVisible,
  missingShots,
  statusForGapList,
  validate,
  type MissingShot,
} from '../../core/validation';
import { answerKey, formSetFor } from '../../core/types';
import type {
  AnswerValue,
  DerivedLine,
  FormSet,
  Gap,
  IssueTally,
  MachineRoom,
  QuestionDef,
  Scope,
  SectionInstance,
  SectionStatus,
  SurveyDocument,
  SurveyStatus,
  ValidationIssue,
} from '../../core/types';
import { isDefaultedKey } from './answerDefaults';

/* ─────────────────────────────── key helpers ────────────────────────────── */

/** The scope an answer to `questionId` is stored under, narrowed from `scope`. */
export function answerScopeFor(questionId: string, scope: Scope = {}): Scope {
  return conditionScope({ questionId }, scope);
}

/** The canonical key for a question in a scope. Never build one by hand. */
export function keyFor(questionId: string, scope: Scope = {}): string {
  return answerKey(questionId, answerScopeFor(questionId, scope));
}

/**
 * The catalog id a derived line prices against. `calc.ts` emits one sub-line id,
 * `H3.overage`, so that the 25-ft overage can be a separate estimate line while
 * still pricing off catalog entry H3 — a dot can never appear in a §3 id, which
 * is what makes this split safe.
 */
function baseQuestionId(questionId: string): string {
  const dot = questionId.indexOf('.');
  return dot < 0 ? questionId : questionId.slice(0, dot);
}

function push<T>(index: Map<string, T[]>, key: string, value: T): void {
  const existing = index.get(key);
  if (existing) existing.push(value);
  else index.set(key, [value]);
}

/* ─────────────────────────────── read helpers ───────────────────────────── */

export function roomOf(doc: SurveyDocument, scope: Scope): MachineRoom | undefined {
  return scope.roomId ? doc.rooms.find((room) => room.id === scope.roomId) : undefined;
}

/** The question set a scope runs — MRL rooms run Hydro (spec Q3). */
export function formSetOf(doc: SurveyDocument, scope: Scope): FormSet | undefined {
  const room = roomOf(doc, scope);
  return room ? formSetFor(room.type) : undefined;
}

/**
 * The questions a section actually asks right now: catalog order, filtered by
 * the room's form set, then by every `showWhen` gate. Structural ids (M1/M2/M3/
 * M6/M7) are dropped — they live in the entity tree and room setup renders them
 * as structure, not as generic question rows.
 *
 * ── The docstring above was true and the code was not (2026-08-18) ─────────
 * The filter it describes lived in TWO callers (`progressFor` below, and
 * `roomSetupQuestions`) and not here, so `QuestionList` — the one consumer that
 * renders — was handed M1–M7 for a room setup section and drew them as generic
 * rows: a hoistway multiselect with no options, a car list with no cars, and
 * §4 rule 7's *"Car 4 isn't in a hoistway yet."* under an empty control, on
 * the screen the hall indicator's "Room Setup" row opened. Chip's second field
 * defect. The filter is here now, once, so no renderer can be handed a
 * structural id: `tests/unit/room-setup-reach.test.ts` refuses it on every
 * section of every document it is given.
 */
export function visibleQuestions(doc: SurveyDocument, section: SectionInstance): QuestionDef[] {
  const formSet = formSetOf(doc, section.scope);
  return questionsFor(section.kind, formSet).filter(
    (question) =>
      !STRUCTURAL_QUESTION_IDS.includes(question.id) && isVisible(question, doc, section.scope),
  );
}

/* ═══════════════════════ what is blocking this section ═══════════════════ */

/**
 * THE APP KNOWS AND MUST SAY — **D-021**.
 *
 * The design authority, live on production, 2026-08-07:
 *
 *   *"In room setup, machine room one has two cars. I said one car has mainline
 *    disconnects to be replaced with new, and the second is being refurbished.
 *    So I've covered both cars, yet it still says the electrical section is
 *    incomplete. Even though 19 of 19 questions have been answered, I don't know
 *    what I'm doing wrong, but I'm stuck."*
 *
 * She was right about everything she could see. §4 rule 2 (mainline: E1a + E1b
 * = cars) was satisfied. What was failing was §4 rule 3 — cab light disconnects,
 * E5a + E5b, an INDEPENDENT rule over two other questions still sitting on the
 * D-016 defaults of No/0 — and the section header said only "in progress" while
 * the chip said "19 of 19 answered". The engine knew the rule, the two question
 * ids, the running total and the target. Not one of those four facts reached the
 * screen.
 *
 * A `SectionBlocker` is that knowledge, made renderable. One list per section,
 * derived from THE SAME GAPS that decide `status`, so the section screen, the
 * hall indicator and the close screen render one explanation and cannot drift:
 * a section cannot be refused for a reason no surface can name, and two surfaces
 * cannot name two different reasons.
 *
 * Note what it is NOT. It computes no new verdict. Every sentence in it is the
 * engine's own — `message` is the §4 issue verbatim, `resolution` is the issue's
 * own `explanation`, and the numbers come from `IssueTally`, never from slicing
 * English back out of a sentence. Nothing here can make a section pass that the
 * gap engine refuses, or refuse one it passes.
 */
export type BlockerKind = 'unanswered' | 'rule' | 'structure' | 'photo';

export interface SectionBlocker {
  /** The engine's own gap id. An acknowledgement is keyed to exactly this. */
  gapId: string;
  sectionInstanceId: string;
  /** Room-qualified, so a blocker reads on its own away from its screen. */
  sectionTitle: string;
  kind: BlockerKind;
  /** Spec §4 rule number when a rule is what is failing — 'V3'. */
  ruleId?: string;
  /** Amendment 8 (D-041) — set only on a `'photo'` blocker: the shot, not a question. */
  shotListId?: string;
  /** Amendment 8 (D-041) — the surveyor's own words for a `'photo'` blocker, off the shot's own note. */
  surveyorNote?: string;
  /**
   * EVERY question the surveyor has to touch to clear this.
   *
   * A rule item is a PAIR, not a field: §4 rules 2 and 3 span replace AND
   * refurbish, and a UI that renders only the first sends the surveyor to fix
   * one number and leaves the other one a mystery. Both ids travel together.
   */
  questionIds: string[];
  /** The short name of the thing that is failing: "Cab light disconnects". */
  title: string;
  /** One short line for a header or a lamp — names the rule and its numbers. */
  headline: string;
  /** The engine's own full sentence. Never paraphrased, never re-derived. */
  message: string;
  /** What would clear it, in the engine's own words. */
  resolution: string;
  /** The running total and the target, when the rule counts something. */
  tally?: IssueTally;
  /**
   * The subset of `questionIds` still carrying the FORM's own answer, which the
   * surveyor never touched (D-016). This is the difference between *"you have
   * not answered this"* and *"this is sitting on the default of No the form
   * opened on, and that leaves a rule unsatisfied"* — the distinction that sent
   * her scanning nineteen fields for a blank that was never there.
   *
   * Kept per question, not as one flag, because a pair can be half-touched: a
   * surveyor who set replace and never looked at refurbish needs to be pointed
   * at refurbish, not at both.
   */
  untouchedQuestionIds: string[];
  /** True when NOTHING in this rule has been touched. */
  untouched: boolean;
}

/* ──────────────────────────── section progress ──────────────────────────── */

export interface SectionProgress {
  status: SectionStatus;
  /** Visible, non-derived questions that carry an answer. */
  answered: number;
  /** Visible, non-derived questions in this section. */
  total: number;
  /** The engine's gaps landing in this section. */
  gaps: Gap[];
  /**
   * Everything standing between this section and `done`, named. Empty exactly
   * when nothing blocks — the same `gaps` `status` is decided from.
   */
  blockers: SectionBlocker[];
}

function plural(count: number, word: string): string {
  return `${String(count)} ${word}${count === 1 ? '' : 's'}`;
}

/**
 * The one short line a header, a lamp or a close-screen row prints.
 *
 * For an equality rule it is the SHAPE OF THE ARITHMETIC, not the complaint:
 * *"Cab light disconnects: 0 of 2 cars accounted for"*. That is the sentence
 * that was missing from her screen — it names the rule, both of its numbers, and
 * nothing else. A ceiling rule has no "of" to state (a remainder is legal), so
 * it prints the engine's own sentence rather than inventing a second phrasing.
 *
 * Exported because the last look renders it LIVE over an `IssueTally` while the
 * surveyor types into the pair inline (D-023). One phrasing of the arithmetic,
 * in one function: a second copy in a `.tsx` is how the section header and the
 * last look would start counting the same rule differently.
 */
export function headlineFor(tally: IssueTally | undefined, message: string): string {
  if (!tally || tally.shape !== 'exact') return message;
  if (tally.running > tally.target) {
    return (
      `${tally.subject}: ${String(tally.running)} accounted for, but there ` +
      `${tally.target === 1 ? 'is' : 'are'} only ${plural(tally.target, tally.unit)}`
    );
  }
  return (
    `${tally.subject}: ${String(tally.running)} of ${String(tally.target)} ` +
    `${tally.unit}${tally.target === 1 ? '' : 's'} accounted for`
  );
}

/**
 * One gap, turned into something a surveyor in a machine room can act on.
 *
 * `issue` is the §4 finding the gap came from, handed over by
 * `detectGapsWithIssues` — the pairing is emitted by the gap engine rather than
 * reconstructed here, so a rule can never be described by a screen using numbers
 * a different pass computed. `missing_photo` returns undefined: §4 rule 8 says
 * photos warn and never block, and a blocker is by definition a refusal.
 *
 * ── Total over blocking reasons, on purpose ─────────────────────────────────
 * For every reason in `BLOCKING_GAP_REASONS` this returns a blocker, always.
 * That is not tidiness, it is the D-021 invariant: `progressFor` derives
 * `blocked` from `blockers`, so a blocking gap that produced NOTHING here would
 * not merely go unexplained — it would let the section read **done** and the
 * lamp go **green** while `projectedStatus` stayed `submitted_with_gaps`.
 * `unanswered`, `missing_required_photo` (Amendment 8, D-041) and
 * `validation_failed` each have their own shape below; anything else that is
 * blocking falls through to `unnamedBlocker`, which says the gap's own
 * sentence rather than vanishing. A new blocking reason therefore ships
 * visible-but-generic instead of invisible-and-wrong.
 */
function unnamedBlocker(section: SectionInstance, gap: Gap): SectionBlocker {
  return {
    gapId: gap.id,
    sectionInstanceId: section.id,
    sectionTitle: gap.sectionTitle || section.title,
    kind: 'rule',
    questionIds: gap.questionId ? [gap.questionId] : [],
    untouchedQuestionIds: [],
    title: gap.sectionTitle || section.title,
    headline: gap.message,
    message: gap.message,
    // Who owns a gap is not restated under it (D-033 R1); the row is the state.
    resolution: '',
    untouched: false,
  };
}

function blockerFor(
  doc: SurveyDocument,
  section: SectionInstance,
  gap: Gap,
  issue: ValidationIssue | undefined,
): SectionBlocker | undefined {
  const scope = section.scope;
  const untouchedIn = (questionIds: readonly string[]): string[] =>
    questionIds.filter((questionId) => isDefaultedKey(doc, keyFor(questionId, scope)));

  // A `surveyor_flagged` gap used to become a blocker of its own here. It cannot
  // reach this function any more: live gaps are the engine's (see
  // `computeResults`), the engine never mints one, and no phone door has raised
  // one since D-033 R15 deleted the card. What stopped the surveyor is a note.

  if (gap.reason === 'unanswered') {
    const questionId = gap.questionId;
    // `detectGaps` always names the question, and since round 8 ruling 1 the
    // engine's gaps are the only ones that reach here — a gap off the record
    // (`doc.gaps`, from the office or another device) never does. `questionId`
    // is still optional on the type, so the unnamed branch stays as the
    // fallback the type demands, carrying its own sentence.
    if (!questionId) return unnamedBlocker(section, gap);
    const label = byId(questionId)?.label ?? questionId;
    const stored = doc.answers[keyFor(questionId, scope)];
    // The one incomplete state that LOOKS answered: Yes with no quantity. The
    // surveyor is told which of the two halves is missing, not just "answer it".
    const yesWithNoCount = stored?.kind === 'flagCount' && stored.value.flag;
    return {
      gapId: gap.id,
      sectionInstanceId: section.id,
      sectionTitle: gap.sectionTitle,
      kind: 'unanswered',
      questionIds: [questionId],
      // Nothing is stored, so nothing can be the form's own default: an
      // unanswered question is a different category, not a quiet one.
      untouchedQuestionIds: [],
      title: label,
      headline: yesWithNoCount ? `${label} — how many?` : `${label} — not answered yet`,
      message: gap.message,
      resolution: yesWithNoCount
        ? 'Enter how many, or set this back to No.'
        : 'This one still needs an answer.',
      untouched: false,
    };
  }

  // Amendment 8 (D-041): a REQUIRED shot with no photo. Named for the shot,
  // not a question — there is nothing to answer, only something to take, or
  // a reason to write (which the engine has already attached).
  if (gap.reason === 'missing_required_photo') {
    const shotListId = gap.id.slice(gap.id.lastIndexOf('#') + 1);
    const shotLabel = section.shotList.find((shot) => shot.id === shotListId)?.label ?? shotListId;
    return {
      gapId: gap.id,
      sectionInstanceId: section.id,
      sectionTitle: gap.sectionTitle,
      kind: 'photo',
      shotListId,
      questionIds: [],
      untouchedQuestionIds: [],
      title: shotLabel,
      headline: `No photo yet of ${shotLabel}`,
      message: gap.message,
      resolution: '',
      untouched: false,
      ...(gap.surveyorNote ? { surveyorNote: gap.surveyorNote } : {}),
    };
  }

  // Anything else that BLOCKS still has to be nameable. `missing_photo` is the
  // one reason that reaches here and must return nothing — §4 rule 8, photos
  // warn and never block — and it is filtered out by the caller before this is
  // ever called, so the guard is against a future reason, not against photos.
  if (gap.reason !== 'validation_failed') {
    return BLOCKING_GAP_REASONS.includes(gap.reason) ? unnamedBlocker(section, gap) : undefined;
  }

  // A validation gap with no issue behind it cannot happen — `detectGaps` mints
  // one only from an issue — but a blocker that cannot name its rule is worse
  // than none, so the gap's own sentence stands in rather than a guess.
  const questionIds = issue ? [...issue.questionIds] : gap.questionId ? [gap.questionId] : [];
  const tally = issue?.tally;
  const title = tally?.subject ?? (questionIds[0] ? byId(questionIds[0])?.label : undefined);
  const untouchedQuestionIds = untouchedIn(questionIds);

  return {
    gapId: gap.id,
    sectionInstanceId: section.id,
    sectionTitle: gap.sectionTitle,
    kind: 'rule',
    ...(issue ? { ruleId: issue.ruleId } : {}),
    questionIds,
    untouchedQuestionIds,
    title: title ?? gap.message,
    headline: headlineFor(tally, gap.message),
    message: gap.message,
    resolution: issue?.explanation ?? 'Check the numbers on this question.',
    ...(tally ? { tally } : {}),
    untouched: questionIds.length > 0 && untouchedQuestionIds.length === questionIds.length,
  };
}

/**
 * A section's lamp state for the hall indicator.
 *
 * "Done" is defined by the GAP ENGINE, not by a second opinion assembled here:
 * a section is done when `detectGaps` has nothing blocking left to say about it
 * and the surveyor has actually answered something. That keeps the lamp and the
 * submit gate in permanent agreement — a room cannot read all-green and then
 * submit `submitted_with_gaps`.
 *
 * Three consequences worth stating:
 *
 *  - Nothing on the RECORD holds the lamp back. A `surveyor_flagged` gap on
 *    `doc.gaps` used to outrank everything; the card that raised one left with
 *    D-033 R15, the blockage is a note, and live results read the engine only
 *    (round 8, task 1). An old flag is the office's to read on the sent record.
 *  - A missing recommended photo does NOT hold the lamp back. §4 rule 8 says
 *    photos warn and never block; they are surfaced on section close and on the
 *    submitted record, which is where they belong.
 *  - Room setup additionally requires the structure to exist. Its remaining
 *    catalog questions are only M4 and M5, so without this a room with no cars,
 *    no groups and no hoistways would light up the moment the surveyor answered
 *    "sprinkler: no" — and the rest of the survey is generated from exactly
 *    that structure (brief §5). It used to hold the lamp back in silence; it
 *    now emits a `structure` blocker, because a refusal nobody can read is the
 *    defect D-021 is about.
 *
 * And the fourth, added by D-021: `status` is decided from `blockers`, not from
 * a second pass over `gaps`. An amber lamp with an empty explanation is now
 * unrepresentable rather than merely unlikely.
 */
function progressFor(
  doc: SurveyDocument,
  section: SectionInstance,
  gaps: Gap[],
  issueByGapId: ReadonlyMap<string, ValidationIssue>,
): SectionProgress {
  // The same exclusions detectGaps applies: structural ids live in the entity
  // tree (already dropped by `visibleQuestions`) and fully-derived ids have no
  // surveyor input to give.
  const asked = visibleQuestions(doc, section).filter(
    (question) => !DERIVED_ONLY_QUESTION_IDS.includes(question.id),
  );

  let answered = 0;
  for (const question of asked) {
    if (isAnsweredFor(question, doc.answers[keyFor(question.id, section.scope)])) answered += 1;
  }

  let structureReady = true;
  if (section.kind === 'room_setup') {
    const room = roomOf(doc, section.scope);
    structureReady =
      room !== undefined &&
      room.cars.length > 0 &&
      room.groups.length > 0 &&
      room.hoistways.length > 0;
  }

  /*
   * The blockers ARE the blocking gaps — same list, one pass, no second opinion.
   *
   * `blocked` is derived from `blockers` rather than from `gaps` a second time,
   * so it is structurally impossible for a section to be refused while the
   * screens have nothing to show for it: an empty `blockers` and a `blocked`
   * status cannot both happen.
   */
  const blockers: SectionBlocker[] = [];
  for (const gap of gaps) {
    if (!BLOCKING_GAP_REASONS.includes(gap.reason)) continue;
    const blocker = blockerFor(doc, section, gap, issueByGapId.get(gap.id));
    if (blocker) blockers.push(blocker);
  }

  /*
   * Room setup's extra condition, said out loud.
   *
   * `structureReady` has always held this section back — the rest of the survey
   * is generated from cars, groups and hoistways (brief §5), so a room with none
   * of them cannot be finished. But it held it back SILENTLY: no gap names it,
   * because M6/M7 live in the entity tree and §4 rule 7 only fires once cars
   * exist to be misplaced. A surveyor who answered M4 and M5 and watched the
   * lamp stay amber had nothing at all to read. Now they do.
   */
  if (section.kind === 'room_setup' && !structureReady) {
    const room = roomOf(doc, section.scope);
    const missing: string[] = [];
    if (!room || room.cars.length === 0) missing.push('cars');
    if (!room || room.groups.length === 0) missing.push('groups');
    if (!room || room.hoistways.length === 0) missing.push('hoistways');
    const list =
      missing.length === 1
        ? missing[0]
        : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1] ?? ''}`;
    /*
     * FIRST, not last (2026-08-18). Room setup is now rendered as a section
     * screen, so `primaryBlocker` — the header chip, the hall indicator's row —
     * headlines this section the way it headlines every other. §4 rule 7 fires
     * for every car in a dimension with NO bucket at all ("Car 1 isn't in a
     * group yet." ×N the moment the first car is added, before the group builder
     * is even on screen), and those gaps stay on the record; but the honest
     * headline for a room that has no groups is that it has no groups, and a
     * surveyor who has only just begun must not read six sentences about cars
     * being nowhere (D-014). Once the structure exists, rule 7 speaks for itself
     * — which is Chip's case exactly, and there it is the right headline.
     */
    blockers.unshift({
      gapId: `${section.id}#structure`,
      sectionInstanceId: section.id,
      sectionTitle: section.title,
      kind: 'structure',
      questionIds: [],
      untouchedQuestionIds: [],
      title: 'Room structure',
      headline: `This room still needs its ${list}`,
      message: `This room has no ${list} yet, and the rest of its survey is built from them.`,
      resolution: `Add the ${list} for this room and the form will build the rest.`,
      untouched: false,
    });
  }

  const blocked = blockers.length > 0;

  let status: SectionStatus;
  if (!blocked && structureReady && answered > 0) status = 'done';
  else if (answered > 0 || (section.kind === 'room_setup' && structureReady)) status = 'in_progress';
  else status = 'todo';

  return { status, answered, total: asked.length, gaps, blockers };
}

/* ═══════════════ the ONE story about whether a section is finished ════════ */

/**
 * **The single answer to "is this section finished?"** — D-021.
 *
 * Every surface calls this. Not `status === 'done'` written out by hand on three
 * screens, not a count of answers, not a lamp colour compared to a string: one
 * function, so the section screen, the hall indicator and the close screen
 * cannot disagree about a section the way the header and the last look disagreed
 * about her Electrical section.
 */
export function isSectionFinished(progress: SectionProgress | undefined): boolean {
  return progress?.status === 'done';
}

/** The blocker a header or a lamp names when it has room for exactly one. */
export function primaryBlocker(
  progress: SectionProgress | undefined,
): SectionBlocker | undefined {
  // The first one is the widest: `progressFor` puts the structure blocker at
  // the head, and the rest follow in the order the engine emitted their gaps.
  return progress?.blockers[0];
}

/** Everything blocking one section, by id. What a screen renders in full. */
export function blockersFor(
  results: SurveyResults,
  sectionInstanceId: string,
): readonly SectionBlocker[] {
  return results.progressBySection.get(sectionInstanceId)?.blockers ?? NO_BLOCKERS;
}

export interface SectionHeadline {
  /** The one sentence the section header prints. Never a bare state label. */
  text: string;
  /**
   * `empty` is NOT `done`. A section that asks nothing has nothing to finish,
   * and `isSectionFinished` says false for it — so a header that painted it
   * green would contradict its own lamp, which is the class of bug D-021 is
   * about. It gets its own neutral tone instead.
   */
  tone: 'done' | 'blocked' | 'progress' | 'empty';
  /**
   * "12 of 19 answered", or ABSENT.
   *
   * THE COUNTER THAT MADE HER DOUBT HERSELF. A tally reading "19 of 19
   * answered" beside a section that will not close is true, useless and
   * corrosive: it invites the surveyor to hunt for a blank that does not exist.
   * So a count of answers is withheld exactly when it would claim a completeness
   * the section does not have — when something is blocking AND every question
   * carries an answer. Everywhere else it is honest and it stays.
   */
  tally?: string;
}

/**
 * What the section header says, for every surface that has a header.
 *
 * The rule, and it is the whole of D-021: **a count of answers is the wrong
 * headline when a rule is failing.** A defaulted answer is a real answer
 * (D-016), and an untouched answer that leaves a §4 rule failing is not a
 * finished one (D-017) — both are true, so the header stops leading with the
 * arithmetic of answeredness and leads with the reason instead.
 */
export function sectionHeadline(progress: SectionProgress | undefined): SectionHeadline {
  // No progress entry at all: not a section this survey builds.
  if (!progress) return { text: 'Nothing is asked in this section.', tone: 'empty' };

  const tally = `${String(progress.answered)} of ${String(progress.total)} answered`;
  const blocker = primaryBlocker(progress);

  if (blocker) {
    return {
      text: blocker.headline,
      tone: 'blocked',
      // Withheld only when it would read as "everything is done" (see above).
      ...(progress.answered < progress.total ? { tally } : {}),
    };
  }

  /*
   * *"Under Ad Hoc Items it says 'Nothing is asked in this section for this
   * room' — this feels like an error."* It is not an error and it is not about
   * a room: Ad Hoc is survey-level and has no room to be empty for. The sentence
   * says what is true of the SECTION and claims nothing about a room.
   */
  if (progress.total === 0) return { text: 'Nothing is asked in this section.', tone: 'empty' };
  if (progress.status === 'done') return { text: tally, tone: 'done', tally };
  return { text: tally, tone: 'progress', tally };
}

/* ═══════════════════════════════ the results ════════════════════════════ */

export interface SurveyResults {
  doc: SurveyDocument;
  /** The whole walk: building, every room's sections, ad hoc last. */
  sections: SectionInstance[];
  derived: DerivedLine[];
  issues: ValidationIssue[];
  /** Everything outstanding, including anything already on the document. */
  gaps: Gap[];
  /** Recommended shots not yet covered (§2.4 — warn, never block). */
  missingShots: MissingShot[];
  /** What the survey WOULD be if submitted now (brief §5). */
  projectedStatus: SurveyStatus;

  /** questionId (narrowed key) → derived lines. Includes `H3.overage` under H3. */
  derivedByKey: Map<string, DerivedLine[]>;
  /** questionId (narrowed key) → validation issues naming it. */
  issuesByKey: Map<string, ValidationIssue[]>;
  sectionById: Map<string, SectionInstance>;
  progressBySection: Map<string, SectionProgress>;
  issuesBySection: Map<string, ValidationIssue[]>;
  gapsBySection: Map<string, Gap[]>;
}

/**
 * Run every engine once and index the output.
 *
 * Called from one place — `SurveyProvider` — on every document change. The four
 * engines are pure functions over a ~100-question document; running all of them
 * per keystroke costs microseconds and buys the guarantee that the chips, the
 * lamps, and the submit gate can never disagree about the same document.
 */
export function computeResults(doc: SurveyDocument): SurveyResults {
  const sections = buildSurveySections(doc);
  const derived = computeDerived(doc);
  const issues = validate(doc);
  // The gap engine hands over which §4 issue each `validation_failed` gap came
  // from. That pairing is what lets a blocker name its rule, carry BOTH of a
  // rule's question ids and print its running total — see `SectionBlocker`.
  const { gaps: detected, issueByGapId } = detectGapsWithIssues(doc);
  const shots = missingShots(doc);

  // Live gaps are the engine's. `doc.gaps` is the record of what was last
  // SENT — kept through an office recall on purpose (src/db/queries.ts) and
  // read by the sealed face and the office, never merged in here. Merging it
  // put a rule the engine no longer failed on a reopened section ("Chips
  // build", 2026-09-04) and turned a flag from a card D-033 deleted into a
  // blocker no screen could lift ("Another test"). Round 8, task 1.
  const gaps = detected;

  const derivedByKey = new Map<string, DerivedLine[]>();
  for (const line of derived) {
    const base = baseQuestionId(line.questionId);
    push(derivedByKey, keyFor(base, line.scope), line);
    if (base !== line.questionId) push(derivedByKey, keyFor(line.questionId, line.scope), line);
  }

  const issuesByKey = new Map<string, ValidationIssue[]>();
  for (const issue of issues) {
    for (const questionId of issue.questionIds) {
      push(issuesByKey, keyFor(questionId, issue.scope), issue);
    }
  }

  const gapsBySection = new Map<string, Gap[]>();
  for (const gap of gaps) push(gapsBySection, gap.sectionInstanceId, gap);

  const sectionById = new Map<string, SectionInstance>();
  const progressBySection = new Map<string, SectionProgress>();
  const issuesBySection = new Map<string, ValidationIssue[]>();

  for (const section of sections) {
    sectionById.set(section.id, section);
    progressBySection.set(
      section.id,
      progressFor(doc, section, gapsBySection.get(section.id) ?? [], issueByGapId),
    );

    // A section's issues are its visible questions' issues. Derived this way,
    // and not by re-deciding which section a rule belongs to, so there is only
    // one mapping from question to section in the codebase.
    const seen = new Set<ValidationIssue>();
    for (const question of visibleQuestions(doc, section)) {
      for (const issue of issuesByKey.get(keyFor(question.id, section.scope)) ?? []) {
        seen.add(issue);
      }
    }
    if (seen.size > 0) issuesBySection.set(section.id, [...seen]);
  }

  return {
    doc,
    sections,
    derived,
    issues,
    gaps,
    missingShots: shots,
    projectedStatus: statusForGapList(gaps),
    derivedByKey,
    issuesByKey,
    sectionById,
    progressBySection,
    issuesBySection,
    gapsBySection,
  };
}

/* ───────────────────────────── lookups on results ───────────────────────── */

const NO_LINES: readonly DerivedLine[] = [];
const NO_ISSUES: readonly ValidationIssue[] = [];
const NO_BLOCKERS: readonly SectionBlocker[] = [];

export function derivedFor(
  results: SurveyResults,
  questionId: string,
  scope: Scope = {},
): readonly DerivedLine[] {
  return results.derivedByKey.get(keyFor(questionId, scope)) ?? NO_LINES;
}

export function issuesFor(
  results: SurveyResults,
  questionId: string,
  scope: Scope = {},
): readonly ValidationIssue[] {
  return results.issuesByKey.get(keyFor(questionId, scope)) ?? NO_ISSUES;
}

export function answerFor(
  doc: SurveyDocument,
  questionId: string,
  scope: Scope = {},
): AnswerValue | undefined {
  return doc.answers[keyFor(questionId, scope)];
}
